// ============================================
// FILE: /frontend/src/components/listings/SharesInterestForm.jsx
// PURPOSE: Form to submit shares purchase interest
// FIXED: Added authentication check and proper toast feedback
// ============================================

import { useState, useEffect } from 'react';
import { Users, Mail, Phone, MessageSquare, Loader2, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { chamaAPI } from '../../api/chama.api';

export default function SharesInterestForm({
  open,
  onOpenChange,
  listingId,
  sharesData, // { sharesWanted, total, deposit, ownership }
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    notes: '',
  });

  // Populate form with user data when authenticated
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        notes: '',
      });
    }
  }, [user]);

  // Check authentication when modal opens
  useEffect(() => {
    if (open && !user) {
      toast({
        variant: 'destructive',
        title: 'Authentication Required',
        description: 'Please log in to submit shares interest.',
      });
      onOpenChange(false);
      navigate('/login', { state: { from: window.location.pathname } });
    }
  }, [open, user, navigate, onOpenChange, toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Double-check authentication
    if (!user) {
      toast({
        variant: 'destructive',
        title: 'Not Authenticated',
        description: 'Please log in to continue.',
      });
      navigate('/login');
      return;
    }

    setLoading(true);

    try {
      const interestData = {
        listingId,
        sharesWanted: sharesData.sharesWanted,
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes || undefined,
      };

      console.log('📤 Submitting shares interest:', interestData);

      const response = await chamaAPI.submitSharesInterest(interestData);

      console.log('✅ Interest submitted successfully:', response);

      toast({
        title: 'Interest Submitted Successfully!',
        description: 'An admin will review your request. Pay the 10% deposit to reserve your shares.',
        duration: 5000,
      });

      // Reset form
      setFormData({
        fullName: user.fullName || user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        notes: '',
      });

      // Close modal
      onOpenChange(false);
      
    } catch (error) {
      console.error('❌ Failed to submit interest:', error);
      
      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Failed to submit interest. Please try again.';

      toast({
        variant: 'destructive',
        title: 'Submission Failed',
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!sharesData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md mx-auto max-h-[90vh] flex flex-col">
        <DialogHeader className="space-y-1.5 sm:space-y-2 pb-2">
          <DialogTitle className="text-lg sm:text-xl md:text-2xl">Reserve Your Shares</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Complete this form to reserve {sharesData.sharesWanted} {sharesData.sharesWanted === 1 ? 'share' : 'shares'}
          </DialogDescription>
        </DialogHeader>

        {/* SCROLLABLE CONTENT */}
        <div className="overflow-y-auto flex-1 px-1">
          {/* Shares Summary */}
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-3 sm:p-4 border border-purple-200 mb-3 sm:mb-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <h4 className="font-semibold text-sm text-purple-900">Your Investment</h4>
            </div>
            <div className="space-y-1.5 text-xs sm:text-sm">
              <div className="flex justify-between">
                <span className="text-purple-700">Shares:</span>
                <span className="font-semibold">{sharesData.sharesWanted}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-purple-700">Ownership:</span>
                <Badge variant="secondary" className="text-xs">{sharesData.ownership}%</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-purple-700">Total Amount:</span>
                <span className="font-semibold">KES {sharesData.total.toLocaleString()}</span>
              </div>
              <div className="flex justify-between pt-1.5 border-t border-purple-300">
                <span className="text-purple-900 font-semibold">Deposit (10%):</span>
                <span className="font-bold text-purple-900">KES {sharesData.deposit.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {/* Full Name */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="fullName" className="text-xs sm:text-sm flex items-center gap-1.5">
                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                Full Name *
              </Label>
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
                className="h-8 sm:h-9 md:h-10 text-xs sm:text-sm"
              />
            </div>

            {/* Email */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="email" className="text-xs sm:text-sm flex items-center gap-1.5">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-8 sm:h-9 md:h-10 text-xs sm:text-sm"
              />
            </div>

            {/* Phone */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="phone" className="text-xs sm:text-sm flex items-center gap-1.5">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                Phone Number *
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="07XXXXXXXX"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="h-8 sm:h-9 md:h-10 text-xs sm:text-sm"
              />
            </div>

            {/* Notes */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="notes" className="text-xs sm:text-sm flex items-center gap-1.5">
                <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                Additional Notes (Optional)
              </Label>
              <Textarea
                id="notes"
                placeholder="Any questions or special requests..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="text-xs sm:text-sm resize-none"
              />
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 text-[10px] sm:text-xs text-blue-800">
              <p className="font-semibold mb-1">Next Steps:</p>
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Submit this form to reserve your shares</li>
                <li>Pay the 10% deposit within 48 hours</li>
                <li>Admin will verify and confirm your reservation</li>
                <li>Full payment due within 90 days of 100% funding</li>
              </ol>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-9 sm:h-10 md:h-11 text-xs sm:text-sm bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin mr-1.5 sm:mr-2" />
                  Submitting...
                </>
              ) : (
                'Reserve Shares'
              )}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}